/**
 * Unified WebSocket Type Definitions
 * 
 * Comprehensive type safety for WebSocket communication between
 * frontend and backend with JWT authentication and channel-based routing.
 */

// ==================== Authentication Types ====================

export interface WebSocketAuthToken {
  token: string;
  expires_at: string;
  user_id: string;
  role: 'admin' | 'user';
}

export interface WebSocketAuthRequest {
  type: 'auth';
  token: string;
}

export interface WebSocketAuthResponse {
  type: 'auth_response';
  success: boolean;
  error?: string;
  user_id?: string;
  channels?: string[];
}

// ==================== Channel Types ====================

export enum WebSocketChannel {
  // Communication channels
  CONVERSATIONS = 'conversations',
  MESSAGES = 'messages',
  TYPING = 'typing',
  
  // Order management channels
  ORDERS = 'orders',
  ORDER_UPDATES = 'order_updates',
  ORDER_VERIFICATION = 'order_verification',
  
  // System channels
  NOTIFICATIONS = 'notifications',
  SYSTEM_ALERTS = 'system_alerts',
  ANALYTICS = 'analytics',
  
  // Admin-specific channels
  ADMIN_BROADCAST = 'admin_broadcast',
  USER_ACTIVITY = 'user_activity'
}

export interface ChannelSubscription {
  channel: WebSocketChannel;
  filters?: Record<string, any>;
  permissions?: string[];
}

export interface ChannelSubscribeRequest {
  type: 'subscribe';
  channel: WebSocketChannel;
  filters?: Record<string, any>;
}

export interface ChannelUnsubscribeRequest {
  type: 'unsubscribe';
  channel: WebSocketChannel;
}

export interface ChannelSubscriptionResponse {
  type: 'subscription_response';
  channel: WebSocketChannel;
  success: boolean;
  error?: string;
}

// ==================== Message Types ====================

export enum WebSocketMessageType {
  // System messages
  AUTH = 'auth',
  AUTH_RESPONSE = 'auth_response',
  SUBSCRIBE = 'subscribe',
  UNSUBSCRIBE = 'unsubscribe',
  SUBSCRIPTION_RESPONSE = 'subscription_response',
  ERROR = 'error',
  PING = 'ping',
  PONG = 'pong',
  
  // Communication messages
  NEW_MESSAGE = 'new_message',
  MESSAGE_READ = 'message_read',
  CONVERSATION_CREATED = 'conversation_created',
  CONVERSATION_UPDATED = 'conversation_updated',
  TYPING_START = 'typing_start',
  TYPING_STOP = 'typing_stop',
  
  // Order messages
  NEW_ORDER = 'new_order',
  ORDER_STATUS_CHANGED = 'order_status_changed',
  ORDER_VERIFICATION_NEEDED = 'verification_needed',
  ORDER_VERIFIED = 'order_verified',
  
  // Notification messages
  NOTIFICATION = 'notification',
  SYSTEM_ALERT = 'system_alert',
  UNREAD_COUNT_UPDATE = 'unread_count_update',
  
  // Analytics messages
  ANALYTICS_UPDATE = 'analytics_update',
  REAL_TIME_METRIC = 'real_time_metric'
}

export interface BaseWebSocketMessage {
  type: WebSocketMessageType;
  timestamp: string;
  id?: string;
  channel?: WebSocketChannel;
}

// ==================== Communication Message Types ====================

export interface NewMessagePayload {
  conversation_id: number;
  message: {
    id: number;
    content: string;
    sender_type: 'user' | 'admin' | 'system';
    sender_id: number;
    sender_name?: string;
    created_at: string;
    message_type?: string;
    metadata?: any;
  };
  user?: {
    id: number;
    first_name: string;
    last_name?: string;
    username?: string;
  };
}

export interface MessageReadPayload {
  conversation_id: number;
  message_ids: number[];
  read_by: number;
  read_at: string;
}

export interface ConversationUpdatedPayload {
  conversation_id: number;
  updates: {
    status?: 'active' | 'resolved' | 'archived';
    priority?: 'low' | 'normal' | 'high' | 'urgent';
    assigned_admin_id?: number;
    tags?: string[];
  };
  updated_by: number;
}

export interface TypingIndicatorPayload {
  conversation_id: number;
  is_typing: boolean;
  user_id: number;
  user_name?: string;
}

// ==================== Order Message Types ====================

export interface NewOrderPayload {
  order_id: number;
  user_id: number;
  total_amount: number;
  status: string;
  items_count: number;
  created_at: string;
  priority: 'normal' | 'high' | 'urgent';
  alert: boolean;
}

export interface OrderStatusChangedPayload {
  order_id: number;
  old_status: string;
  new_status: string;
  changed_by: number;
  changed_at: string;
  reason?: string;
}

export interface OrderVerificationPayload {
  order_id: number;
  variance_percent: number;
  priority: 'high' | 'urgent';
  alert: boolean;
  verification_details: {
    expected_weight: number;
    actual_weight: number;
    items_affected: Array<{
      product_id: number;
      product_name: string;
      expected_weight: number;
      actual_weight: number;
    }>;
  };
}

// ==================== Notification Message Types ====================

export interface NotificationPayload {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  actions?: Array<{
    label: string;
    action: string;
    data?: any;
  }>;
  expires_at?: string;
}

export interface SystemAlertPayload {
  id: string;
  level: 'info' | 'warning' | 'error' | 'critical';
  title: string;
  message: string;
  source: string;
  details?: any;
  requires_acknowledgment: boolean;
}

export interface UnreadCountUpdatePayload {
  conversation_id?: number;
  total_unread: number;
  urgent_unread: number;
  by_priority: {
    low: number;
    normal: number;
    high: number;
    urgent: number;
  };
}

// ==================== Analytics Message Types ====================

export interface AnalyticsUpdatePayload {
  metric_type: string;
  value: number;
  timestamp: string;
  dimensions?: Record<string, any>;
}

export interface RealTimeMetricPayload {
  metric_name: string;
  current_value: number;
  previous_value?: number;
  change_percent?: number;
  trend: 'up' | 'down' | 'stable';
  category: 'orders' | 'revenue' | 'customers' | 'performance';
}

// ==================== Unified Message Type ====================

export type WebSocketMessage =
  | (BaseWebSocketMessage & WebSocketAuthRequest)
  | (BaseWebSocketMessage & WebSocketAuthResponse)
  | (BaseWebSocketMessage & ChannelSubscribeRequest)
  | (BaseWebSocketMessage & ChannelUnsubscribeRequest)
  | (BaseWebSocketMessage & ChannelSubscriptionResponse)
  | (BaseWebSocketMessage & { type: WebSocketMessageType.NEW_MESSAGE; payload: NewMessagePayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.MESSAGE_READ; payload: MessageReadPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.CONVERSATION_UPDATED; payload: ConversationUpdatedPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.TYPING_START | WebSocketMessageType.TYPING_STOP; payload: TypingIndicatorPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.NEW_ORDER; payload: NewOrderPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.ORDER_STATUS_CHANGED; payload: OrderStatusChangedPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.ORDER_VERIFICATION_NEEDED; payload: OrderVerificationPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.NOTIFICATION; payload: NotificationPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.SYSTEM_ALERT; payload: SystemAlertPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.UNREAD_COUNT_UPDATE; payload: UnreadCountUpdatePayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.ANALYTICS_UPDATE; payload: AnalyticsUpdatePayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.REAL_TIME_METRIC; payload: RealTimeMetricPayload })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.ERROR; error: string; code?: string })
  | (BaseWebSocketMessage & { type: WebSocketMessageType.PING | WebSocketMessageType.PONG });

// ==================== Connection State Types ====================

export enum WebSocketConnectionState {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  AUTHENTICATING = 'authenticating',
  AUTHENTICATED = 'authenticated',
  RECONNECTING = 'reconnecting',
  ERROR = 'error'
}

export interface WebSocketConnectionInfo {
  state: WebSocketConnectionState;
  url: string;
  protocol: string;
  connectedAt?: Date;
  lastHeartbeat?: Date;
  reconnectAttempts: number;
  maxReconnectAttempts: number;
  reconnectDelay: number;
  subscribedChannels: Set<WebSocketChannel>;
}

// ==================== Event Handler Types ====================

export type WebSocketEventHandler<T = any> = (payload: T, message: WebSocketMessage) => void;

export interface WebSocketEventHandlers {
  onConnect?: () => void;
  onDisconnect?: (reason: string) => void;
  onError?: (error: Error) => void;
  onAuthSuccess?: (user_id: string, channels: string[]) => void;
  onAuthFailure?: (error: string) => void;
  onMessage?: WebSocketEventHandler<any>;
  onChannelSubscribed?: (channel: WebSocketChannel) => void;
  onChannelUnsubscribed?: (channel: WebSocketChannel) => void;
  
  // Message type specific handlers
  [WebSocketMessageType.NEW_MESSAGE]?: WebSocketEventHandler<NewMessagePayload>;
  [WebSocketMessageType.MESSAGE_READ]?: WebSocketEventHandler<MessageReadPayload>;
  [WebSocketMessageType.CONVERSATION_UPDATED]?: WebSocketEventHandler<ConversationUpdatedPayload>;
  [WebSocketMessageType.TYPING_START]?: WebSocketEventHandler<TypingIndicatorPayload>;
  [WebSocketMessageType.TYPING_STOP]?: WebSocketEventHandler<TypingIndicatorPayload>;
  [WebSocketMessageType.NEW_ORDER]?: WebSocketEventHandler<NewOrderPayload>;
  [WebSocketMessageType.ORDER_STATUS_CHANGED]?: WebSocketEventHandler<OrderStatusChangedPayload>;
  [WebSocketMessageType.ORDER_VERIFICATION_NEEDED]?: WebSocketEventHandler<OrderVerificationPayload>;
  [WebSocketMessageType.NOTIFICATION]?: WebSocketEventHandler<NotificationPayload>;
  [WebSocketMessageType.SYSTEM_ALERT]?: WebSocketEventHandler<SystemAlertPayload>;
  [WebSocketMessageType.UNREAD_COUNT_UPDATE]?: WebSocketEventHandler<UnreadCountUpdatePayload>;
  [WebSocketMessageType.ANALYTICS_UPDATE]?: WebSocketEventHandler<AnalyticsUpdatePayload>;
  [WebSocketMessageType.REAL_TIME_METRIC]?: WebSocketEventHandler<RealTimeMetricPayload>;
}

// ==================== Configuration Types ====================

export interface WebSocketConfig {
  url?: string;
  token?: string;
  autoConnect?: boolean;
  autoReconnect?: boolean;
  maxReconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
  authTimeout?: number;
  messageTimeout?: number;
  protocols?: string[];
  headers?: Record<string, string>;
}

// ==================== Utility Types ====================

export interface WebSocketError {
  code: string;
  message: string;
  details?: any;
  timestamp: Date;
}

export interface WebSocketMetrics {
  messagesReceived: number;
  messagesSent: number;
  reconnections: number;
  uptime: number;
  lastActivity: Date;
  averageLatency: number;
}

// ==================== Export All Types ====================
// All types are already exported individually above